import { reactive, type UnwrapRef } from 'vue'
import { hashAny } from '../hash'
import { sleep } from '../time'
import { smoothstep } from '../math'

export const tryDo = async <T = any>(
  func: () => Promise<T> | T,
  onError?: (err: Error) => void
): Promise<T | undefined> => {
  try {
    return await func()
  } catch (e) {
    if (onError) {
      onError(e as Error)
    }
    return undefined
  }
}

export type AsyncJobEntry<T = any> = {
  promise?: Promise<T | undefined>
  isLoading: boolean
  error?: any
  value?: T
}
export type AsyncMap<T = any> = {
  [key: string]: AsyncJobEntry<T | undefined>
}

const asyncJobs: AsyncMap = reactive<AsyncMap<unknown>>({})

export type DoAsyncOptions = {
  delayMs?: number
  key?: string
}

export const doAsync = <T = any>(
  fun: () => Promise<T>,
  options: DoAsyncOptions = {}
): AsyncJobEntry<T> | UnwrapRef<AsyncJobEntry<T>> => {
  const key = options.key || hashAny(fun.toString())
  if (asyncJobs[key] && asyncJobs[key].isLoading) {
    return asyncJobs[key]
  }

  const job = async (): Promise<T | undefined> => {
    if (!asyncJobs[key]) return
    asyncJobs[key].isLoading = true
    try {
      if (options.delayMs) {
        await sleep(options.delayMs)
      }
      const result = await fun()
      asyncJobs[key].isLoading = false
      asyncJobs[key].value = result
      return result
    } catch (e) {
      asyncJobs[key].isLoading = false
      asyncJobs[key].error = e
      throw e
    }
  }

  asyncJobs[key] = reactive<AsyncJobEntry<T | undefined>>({
    isLoading: true
  })

  job().catch((e) => console.error(e))

  return asyncJobs[key]
}

export type AsyncFun<T = any> = () => Promise<T>
export type ProgressCallback = (elapsed: number, index: number, name?: string) => any | void

export class AsyncJob<T = any> {
  name?: string
  index: number = 0
  fun: AsyncFun<T>
  done: boolean = false
  error: unknown = undefined
  result: T | null | undefined = undefined
  timeStarted: Date = new Date()
  elapsed: number = 0
  id: number = -1
  delay?: number

  constructor(fun: AsyncFun<T>, index?: number, name?: string, delay?: number) {
    this.fun = fun
    this.index = index || 0
    this.name = name
    this.delay = delay
  }

  run(callback?: ProgressCallback) {
    return new Promise<T>((resolve, reject) => {
      const procedure = async (): Promise<T> => {
        this.timeStarted = new Date()

        this.id = window.setInterval(() => {
          const now = new Date()
          this.elapsed = now.getTime() / 1000.0 - this.timeStarted.getTime() / 1000

          if (callback) {
            callback(this.elapsed, this.index, this.name)
          }

          if (this.done || this.error) {
            console.log({ done: this.done, error: this.error })
            clearTimeout(this.id)
            this.id = -1
            if (this.error) return reject(this.error)
            return resolve(this.result as any)
          }
        })

        if (this.delay) {
          await sleep(this.delay)
        }

        try {
          this.result = await this.fun()
        } catch (e) {
          this.error = e
        }

        this.done = true

        return this.result as any
      }

      procedure().catch((e) => console.error(e))
    })
  }
}

export type JobInfo = { elapsed: number; percent: number; done?: boolean }
export type AsyncJobInfoDict = { [key: string]: JobInfo }
export type DoAsyncWithProgressJob<T = any> = { name?: string; fun: AsyncFun<T>; delay?: number }
export type DoAsyncWithProgressCallback = (
  elapsed: number,
  percent: number,
  jobs: AsyncJobInfoDict
) => void | any

export const doAsyncWithProgress = <T = any>(
  funs: DoAsyncWithProgressJob<T>[],
  progressCallback?: DoAsyncWithProgressCallback
) => {
  return new Promise<T[]>((resolve, reject) => {
    const procedure = async () => {
      const items = funs.map((fun, i) => new AsyncJob<T>(fun.fun, i, fun.name, fun.delay))
      const timeStarted = new Date()
      let results: T[] = []
      let doneCount = 0
      let totalMaxElapsed = 0

      if (progressCallback) {
        const interval = setInterval(() => {
          const now = new Date()
          const elapsed = now.getTime() / 1000.0 - timeStarted.getTime() / 1000

          const elapses = items.map((it) => it.elapsed)
          const maxElapsed = Math.max(...elapses)
          totalMaxElapsed = Math.max(totalMaxElapsed, maxElapsed)

          progressCallback(
            elapsed,
            doneCount / items.length,
            Object.assign(
              {},
              ...items
                .filter((it) => !it.done)
                .map((it, i) => {
                  const coeff = it.done
                    ? 0
                    : 0.5 * smoothstep(0.1 + totalMaxElapsed * items.length, 0.0, it.elapsed)
                  const fraction = it.elapsed / (Math.max(1.0, totalMaxElapsed) + coeff)
                  const percent = smoothstep(0.0, 1.0, fraction)

                  return {
                    [it.name || `${i}`]: {
                      elapsed: it.elapsed,
                      done: it.done,
                      percent: percent
                    }
                  }
                })
            )
          )

          if (doneCount >= items.length) {
            clearInterval(interval)
          }
        }, 0)
      }

      const callback = (elapsed: number, index: number, name?: string) => {
        console.log('TODO: do we really need these? ', { elapsed, index, name })
        let nextDoneCount = 0
        for (const job of items) {
          if (job.done || job.error) nextDoneCount += 1
        }

        doneCount = nextDoneCount
        if (doneCount >= items.length) {
          resolve(results)
        }
      }

      const promises = items.map((it) => it.run(callback))

      try {
        results = await Promise.all(promises)
      } catch (e) {
        reject(e)
      }

      return results
    }

    procedure().catch((e) => console.error(e))
  })
}
